import { useCallback, useMemo } from "react";
import { isEmpty } from "lodash";
import { GeneralBannerType } from "../features/Features_Cms/CmsGlobal.Types";
import {
  useLazyGenerateLmsLeadQuery,
  useLazyLmsLeadRedirectionQuery,
} from "../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import {
  useAppDispatch,
  useAppSelector,
} from "../redux-store/CustomReduxHooks";
import { setError } from "../redux-store/reducers/CsErrorSlice";
import { API_STATUS_CODE } from "../utils/ApiEndpoints";
import {
  ABCDService,
  ABCD_SUPER_APP_PLAYSTORE_URL,
  androidType,
  centerBannerName,
  iOSType,
  l2BannerName,
  serviceApp,
  serviceMob,
  serviceWeb,
} from "../utils/constants";
import { getMobileOperatingSystem, windowOpen } from "../utils/helper";
import { usePlatformFlags } from "./utils/bannerFilterUtils";
import dayjs from "dayjs";

const useLeadUtilities = (bannerName: string) => {
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const userDetails = csAppSlice.userDetails;
  const tempLeadNameFromSlice = useAppSelector(
    (state) => state.CsAppSlice.tempLeadName,
  );
  const os = useMemo(() => getMobileOperatingSystem(), []);
  const dispatch = useAppDispatch();

  const isPromotionalCard = useMemo(
    () => [l2BannerName, centerBannerName].includes(bannerName),
    [bannerName],
  );

  const [generateLmsLeadId] = useLazyGenerateLmsLeadQuery();
  const [getLmsLeadRedirectionToken] = useLazyLmsLeadRedirectionQuery();

  const platformFlags = usePlatformFlags(
    csAppSlice.channel,
    csAppSlice.NativeDeviceInfo?.deviceName,
  );

  const getCustomerName = useCallback(() => {
    return (
      (isEmpty(userDetails?.name)
        ? isEmpty(userDetails?.tempLeadName)
          ? isEmpty(tempLeadNameFromSlice)
            ? ""
            : tempLeadNameFromSlice
          : userDetails?.tempLeadName
        : userDetails?.name) || ""
    );
  }, [userDetails?.name, userDetails?.tempLeadName, tempLeadNameFromSlice]);

  const getSubSource = useCallback(() => {
    if (platformFlags.isMobileApp) return serviceApp;
    if (platformFlags.isSuperApp) return ABCDService;

    if (os === iOSType || os === androidType) return serviceMob;
    if (platformFlags.isWeb) return serviceWeb;

    return "";
  }, [
    platformFlags.isMobileApp,
    platformFlags.isSuperApp,
    platformFlags.isWeb,
    os,
  ]);

  const windowOpenRedirection = useCallback((url: string = "") => {
    if (!isEmpty(url)) windowOpen(url);
  }, []);

  const sendMessageToNative = useCallback(
    (message: any) => {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify(message));
        return true;
      }
      return false;
    },
    [window.ReactNativeWebView],
  );

  // --- Reusable destination openers (remove promo-vs-normal duplication)
  const openWebDestination = useCallback(
    (url: string) => {
      if (isPromotionalCard) {
        windowOpen(url, "_blank", "noopener,noreferrer");
      } else {
        windowOpenRedirection(url);
      }
    },
    [isPromotionalCard, windowOpenRedirection],
  );

  const handlePromotionalCardClickForMobile = useCallback(
    (link: string) => {
      if (os === iOSType) {
        window.location.href = link;
        return;
      }

      if (window.ReactNativeWebView) {
        const message = {
          action: "TOGGLE_APP",
          playStoreUrl: ABCD_SUPER_APP_PLAYSTORE_URL,
          urlScheme: link,
        };
        window.ReactNativeWebView.postMessage(JSON.stringify(message));
      }
    },
    [os],
  );

  const openAppDestination = useCallback(
    (url: string) => {
      if (isPromotionalCard) {
        handlePromotionalCardClickForMobile(url);
      } else {
        windowOpenRedirection(url);
      }
    },
    [
      isPromotionalCard,
      handlePromotionalCardClickForMobile,
      windowOpenRedirection,
    ],
  );

  const generateLmsLeadIdForAppsFlyerRedirection = useCallback(
    async (data: GeneralBannerType, buyUserEnteredName?: string) => {
      await generateLmsLeadId({
        name: getCustomerName() || buyUserEnteredName || "",
        mobile: userDetails?.mobileNumber || "",
        email: userDetails?.emailId || "",
        utmSource: "",
        subSource: getSubSource(),
        lob: data?.attributes?.productCode || "",
        productCategory: data?.attributes?.productCategory || "",
        vemAssignmentCheck: true,
        asyncLeadCreation: true,
      });
    },
    [
      generateLmsLeadId,
      getCustomerName,
      getSubSource,
      userDetails?.mobileNumber,
      userDetails?.emailId,
    ],
  );

  const generateLeadIdAndTokenForLeadRedirection = useCallback(
    async (data: GeneralBannerType, buyJourneyUserName?: string) => {
      const validName = getCustomerName() || buyJourneyUserName || "";
      const url = data?.attributes?.ssoUrl || "";

      // ETB onBoarded users without name
      if (isEmpty(validName) && !isEmpty(url)) {
        windowOpen(url, "_self", "noopener,noreferrer");
        return;
      }

      if (isEmpty(validName)) return;

      try {
        const leadResp = await generateLmsLeadId({
          name: validName,
          mobile: userDetails?.mobileNumber || "",
          email: userDetails?.emailId || "",
          utmSource: "",
          subSource: getSubSource(),
          lob: data?.attributes?.productCode || "",
          productCategory: data?.attributes?.productCategory || "",
          vemAssignmentCheck: true,
          asyncLeadCreation: false,
        });

        if (
          leadResp.isError ||
          leadResp.data?.code !== API_STATUS_CODE.SUCCESS
        ) {
          dispatch(
            setError({
              error: new Error("Error"),
              message: "LMS Lead Generate API failed",
            }),
          );
          return;
        }

        const lmsId = leadResp?.data?.payload?.lmsId || "";

        const tokenResp = await getLmsLeadRedirectionToken({
          username: validName,
          mobile: userDetails?.mobileNumber || "",
          productId: data?.attributes?.productCode || "",
          lmsId,
          sourceOrigin: getSubSource(),
        });

        if (
          tokenResp.isError ||
          tokenResp.data?.code !== API_STATUS_CODE.SUCCESS
        ) {
          dispatch(
            setError({
              error: new Error("Error"),
              message: "Lead Redirection Token API failed",
            }),
          );
          return;
        }

        const token = tokenResp?.data?.payload?.uuid || "";
        const finalLink = url + `?token=${token}&isServiceApp=true`;
        windowOpen(finalLink, "_self", "noopener,noreferrer");
      } catch (err) {
        dispatch(
          setError({
            error: err as Error,
            message: "Unexpected error in web redirection flow",
          }),
        );
      }
    },
    [
      dispatch,
      generateLmsLeadId,
      getLmsLeadRedirectionToken,
      getCustomerName,
      getSubSource,
      userDetails?.mobileNumber,
      userDetails?.emailId,
    ],
  );

  const lmsLeadIdWithCallBackTimeC = useCallback(
    async (data: GeneralBannerType) => {
      return await generateLmsLeadId({
        name: getCustomerName() || "",
        mobile: userDetails?.mobileNumber || "",
        email: userDetails?.emailId || "",
        utmSource: "",
        subSource: getSubSource(),
        lob: data?.attributes?.productCode || "",
        productCategory: data?.attributes?.productCategory || "",
        vemAssignmentCheck: true,
        asyncLeadCreation: true,
        callbackTimeC: dayjs().format(),
      }).then((res) => {
        if (res.isError || res.data?.code !== API_STATUS_CODE.SUCCESS) {
          dispatch(
            setError({
              error: new Error("Error"),
              message: "LMS Lead Generate API failed",
            }),
          );
          return false;
        }
        if (res.data?.code === API_STATUS_CODE.SUCCESS) {
          return true;
        }
        return false;
      });
    },
    [
      generateLmsLeadId,
      getCustomerName,
      getSubSource,
      userDetails?.mobileNumber,
      userDetails?.emailId,
    ],
  );

  const proceedAppRedirect = useCallback(
    async ({
      data,
      buyJourneyUserName,
      isAppLead,
      appsFlyerLink,
    }: {
      data: GeneralBannerType;
      buyJourneyUserName?: string;
      isAppLead: boolean;
      appsFlyerLink: string;
    }) => {
      if (isAppLead) {
        await generateLeadIdAndTokenForLeadRedirection(
          data,
          buyJourneyUserName,
        );
        return;
      }

      generateLmsLeadIdForAppsFlyerRedirection(data, buyJourneyUserName);
      openAppDestination(appsFlyerLink);
    },
    [
      generateLeadIdAndTokenForLeadRedirection,
      generateLmsLeadIdForAppsFlyerRedirection,
      openAppDestination,
    ],
  );

  return {
    generateLmsLeadIdForAppsFlyerRedirection,
    generateLeadIdAndTokenForLeadRedirection,
    openAppDestination,
    openWebDestination,
    proceedAppRedirect,
    sendMessageToNative,
    lmsLeadIdWithCallBackTimeC,
  };
};

export default useLeadUtilities;
